import { TranslateService } from 'Public_old/src/app/Common/Services/translate.service';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EnvironmentInjector,
	EventEmitter,
	forwardRef,
	Injector,
	Input,
	inject,
	Output,
	Self,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, UntypedFormControl } from '@angular/forms';
import { GlobalValidator } from '../Classes/globalValidator';

interface MyMonth {
	m: number;
	y: number;
}

interface Day {
	date: Date;
	day: number;
	month: number;
	year: number;
	sun: boolean;
}

interface dateSpan {
	editable: boolean;
	from: Date;
	to: Date;
}

export class DatepickerOptions {
	show: 'calendar' | 'input';
	modelFormat: 'singleDate' | 'timeSpan';
	spanSize: { min: number; max: number };
	lockSpanStartDate: boolean;
	numberOfSpans: number;

	constructor(overrideOptions) {
		this.show = overrideOptions.show ? overrideOptions.show : 'input';
		this.modelFormat = overrideOptions.modelFormat ? overrideOptions.modelFormat : 'singleDate';
		this.spanSize = overrideOptions.spanSize ? overrideOptions.spanSize : { min: 1, max: 999 };
		this.numberOfSpans = overrideOptions.numberOfSpans ? overrideOptions.numberOfSpans : 1;
		this.lockSpanStartDate = overrideOptions.lockSpanStartDate ? overrideOptions.lockSpanStartDate : false;
	}
}

@Component({
	standalone: false,
	selector: 'visiba-datepicker',
	template: /*language=html*/ `
		<div
			class="inputWrap wallInput wallStyling"
			[class.hasIcon]="icon != null"
			[tabindex]="tabInd"
			(focus)="openCalendar()"
			*ngIf="options().show === 'input'"
		>
			<input type="text" readonly="true" [(ngModel)]="selectedDateText" [placeholder]="localPlaceholder" (click)="openCalendar()" />
			<div class="svgWrap" *ngIf="selectedDateText != null && !readonly && clearButton" (click)="deselect()">
				<visiba-svg a="X"></visiba-svg>
			</div>
			<visiba-svg *ngIf="icon != null" class="iconSvg" [a]="icon"></visiba-svg>
			<label [class.value]="selectedDateText != null && selectedDateText != ''" [class.svg]="icon != null">{{
				localPlaceholder
			}}</label>
			<span class="error" [class.value]="selectedDateText != null && selectedDateText != ''">{{ currentError }}</span>
		</div>
		<div class="calendar" *ngIf="currentView != null" [ngStyle]="calendarStyle">
			<div class="header">
				<span class="prev" (click)="changeMonth(-1)">
					<svg viewBox="0 0 32.9 56.7">
						<polygon
							class="fill"
							points="29.5 0 0.1 28.3 0 28.3 0.05 28.35 0 28.4 0.1 28.4 29.5 56.7 32.9 52.2 9.15 28.35 32.9 4.5 29.5 0"
						/>
					</svg>
				</span>
				{{ monthNames[visibleMonth.m - 1] }} {{ visibleMonth.y }}
				<span class="next" (click)="changeMonth(1)">
					<svg viewBox="0 0 32.9 56.7">
						<polygon points="3.4 56.7 32.8 28.4 32.9 28.4 32.85 28.35 32.9 28.3 32.8 28.3 3.4 0 0 4.5 23.75 28.35 0 52.2 3.4 56.7" />
					</svg>
				</span>
			</div>
			<table class="caltable">
				<thead>
					<tr>
						<th *ngFor="let d of dayNames">{{ d.substring(0, 1) }}</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngFor="let w of dates">
						<td *ngFor="let d of w" (click)="selectDate(d.date)" [ngClass]="getDateSpanClasses(d.date)">
							<div
								[ngClass]="{
									isInvalid: !isValid(d.date),
									isToday: isToday(d.date),
									isSelected: isSelected(d.date),
									isOld: false,
									isOtherMonth: d.month != visibleMonth.m,
								}"
							>
								{{ d.day }}
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	`,
	host: {
		'[class]': '"show_" + options().show',
		'[class.readonly]': 'readonly',
	},
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VisibaDatepickerComponent implements ControlValueAccessor {
	@Input() tabInd: number = 0;
	@Input() icon: string = null;
	@Input() placeholder: string = '';

	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	@Input('options') overrideOptions: any = {};

	@Input() maxView: string = 'year';
	@Input() minView: string = 'minute';
	@Input() minDate: Date | string = null;
	@Input() maxDate: Date | string = null;
	@Input() sunFirst: boolean = false; // First week day
	@Input() readonly: boolean = false;
	@Input() errorMessages: { [name: string]: string } | string = 'default';
	@Input() dateFormat: string = 'yyyy-MM-dd HH:mm';
	@Input() clearButton: boolean = true;
	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	@Output() modelChange: any;

	views: Array<string> = ['year', 'month', 'day', 'hour', 'minute', 'second'];
	currentView: string = null;
	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	calendarStyle: any = {};
	currentDateSpanIndex: number = 0;

	monthNames: Array<string> = µ.Env.i18n.monthNames.slice(12, 24);
	dayNames: Array<string> = µ.Env.i18n.dayNames.slice(0, 7);

	localPlaceholder: string = null;
	currentError: string = null;
	visibleMonth: MyMonth = { m: 0, y: 0 };
	selectedDate: Date = null;
	dateSpans: Array<dateSpan> = [];
	selectedDateText: string = null;
	today: Date = null;
	dates: Array<Array<Day>> = [];

	constructor(
		public elem: ElementRef,
		@Self() public controlDir: NgControl,
		private _changeDetector: ChangeDetectorRef,
		private translateService: TranslateService,
	) {
		controlDir.valueAccessor = this;
		this.today = new Date();
		this.modelChange = new EventEmitter();
	}

	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	writeValue(value: any) {
		window.setTimeout(() => {
			this.handleErrors();
		});
		if (value !== undefined && value !== null) {
			try {
				if (this.options().modelFormat === 'singleDate') {
					const d = new Date(value);
					this.selectDate(d, false);
				} else {
					this.dateSpans = value;
				}
				// biome-ignore lint/correctness/noUnusedVariables: suppressed due to migration - should be fixed
			} catch (e) {
				this.dateSpans = [];
				this.selectedDate = null;
				this.selectedDateText = null;
				this.closeCalendar();
			}
		} else {
			this.dateSpans = [];
			this.selectedDate = null;
			this.selectedDateText = null;
			this.closeCalendar();
		}
		this.currentDateSpanIndex = 0;
		this._changeDetector.markForCheck(); // Start check here otherwise errors when using datepicket in cipyfunction office calendar, 2017-12-20
	}

	// Call to push value
	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	// biome-ignore lint/suspicious/noEmptyBlockStatements: suppressed due to migration - should be fixed
	propagateChange = (_: any) => {};

	registerOnChange(fn) {
		this.propagateChange = fn;
	}

	// biome-ignore lint/suspicious/noEmptyBlockStatements: suppressed due to migration - should be fixed
	registerOnTouch() {}
	// biome-ignore lint/suspicious/noEmptyBlockStatements: suppressed due to migration - should be fixed
	registerOnTouched() {}

	ngOnInit() {
		if (this.minDate === 'now') this.minDate = new Date();

		if (!this.sunFirst) this.dayNames.push(this.dayNames.splice(0, 1)[0]);

		if (this.placeholder !== null && this.placeholder !== '') {
			this.localPlaceholder = this.translateService.get(this.placeholder) + (this.elem.nativeElement.required ? ' *' : '');
		}

		// TODO
		// Set correct first day of week
		// Set Min/max view
	}

	ngAfterViewInit() {
		if (this.options().show === 'calendar') this.openCalendar();
	}

	// biome-ignore lint/suspicious/noEmptyBlockStatements: suppressed due to migration - should be fixed
	ngOnDestroy() {}

	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	emitValue(value: any) {
		this.modelChange.emit(value);
		this.propagateChange(value);

		this.handleErrors();
	}

	openCalendar(): void {
		if (this.readonly || this.currentView !== null) return;

		this.currentView = this.maxView;

		let startDate = new Date();

		if (this.selectedDate !== null) startDate = this.selectedDate;
		else if (this.dateSpans && this.dateSpans[0] && this.dateSpans[0].from) startDate = this.dateSpans[0].from;

		this.visibleMonth = { m: startDate.getMonth() + 1, y: startDate.getFullYear() };

		// Create current month
		this.createMonth(this.visibleMonth.m, this.visibleMonth.y);

		if (this.options().show === 'calendar') return;

		window.setTimeout(() => {
			const doc = document.getElementsByTagName('html')[0];
			doc.addEventListener('click', this.calClickCheck, false);
		});

		const elRect = this.elem.nativeElement.getClientRects()[0];
		if (!elRect) return;
		const calWidth = elRect.left + elRect.width / 2;
		if (calWidth < 125) {
			this.calendarStyle = { 'margin-left': 125 - calWidth - 135 + 'px' };
		} else {
			this.calendarStyle = {};
		}

		this._changeDetector.detectChanges();
	}

	calClickCheck = (event) => {
		if (
			this.currentView !== null &&
			event.target &&
			this.elem.nativeElement !== event.target &&
			!this.elem.nativeElement.contains(event.target)
		) {
			this.closeCalendar();
		}
	};

	closeCalendar(): void {
		if (this.options().show === 'calendar') return;
		this.currentView = null;
		const doc = document.getElementsByTagName('html')[0];
		doc.removeEventListener('click', this.calClickCheck, false);

		this._changeDetector.detectChanges();
	}

	changeMonth(go: number): void {
		this.visibleMonth.m = this.visibleMonth.m + go;

		if (this.visibleMonth.m === 13) {
			this.visibleMonth.m = 1;
			this.visibleMonth.y++;
		} else if (this.visibleMonth.m === 0) {
			this.visibleMonth.m = 12;
			this.visibleMonth.y--;
		}

		this.createMonth(this.visibleMonth.m, this.visibleMonth.y);
	}

	changeYear(go: number): void {
		this.visibleMonth.y = this.visibleMonth.y + go;
		this.createMonth(this.visibleMonth.m, this.visibleMonth.y);
	}

	selectDate(date: Date, emit: boolean = true): void {
		if (this.options().modelFormat !== 'singleDate') {
			this.selectDateInSpan(date, emit);
			return;
		}

		if (!this.isValid(date)) return;

		this.selectedDate = date;
		this.selectedDateText = date.format(this.dateFormat);

		this.closeCalendar(); // TODO: Set next if not minView

		if (emit) this.emitValue(this.selectedDate);
	}

	selectDateInSpan(date: Date, emit: boolean = true, isSystemSet = false): void {
		if (!this.isValid(date)) return;

		this.validateSpanIndex();
		const currentSpan = this.dateSpans[this.currentDateSpanIndex];

		// If date is in span, remove it
		if (this.removeDateInSpan(date)) {
			if (isSystemSet) {
				// After remove crash rerun to set TO value of fixed span length :316
				this.selectDateInSpan(date, emit, isSystemSet);
			}

			return;
		}

		if (currentSpan.from === null || currentSpan.to !== null || date.getTime() < currentSpan.from.getTime()) {
			// If lockSpanStartDate move to day befre that matches
			if (this.options().lockSpanStartDate && this.dateSpans[0].from.getDay() !== date.getDay()) {
				const diff = date.getDate() - (date.getDay() - this.dateSpans[0].from.getDay());
				date = new Date(new Date(date).setDate(diff));
			}

			currentSpan.from = date;
			currentSpan.to = null;
			if (this.options().spanSize.min === this.options().spanSize.max) {
				// If spanSize is fixed. Set it directly
				this.selectDateInSpan(new Date(date.getTime() + (this.options().spanSize.min - 1) * 24 * 60 * 60 * 1000), emit, true);
				return;
			}
		} else {
			if (!this.validateSpanSelection(currentSpan.from, date)) return;

			currentSpan.to = date;
			this.increaseSpanIndex();
			this.validateSpanIndex();
		}

		if (emit) this.emitValue(this.dateSpans);
	}

	private removeDateInSpan(date: Date): boolean {
		let result = false;
		this.dateSpans.forEach((span, index) => {
			if (
				span.from &&
				span.to &&
				span.from.format('yyyyMMdd') <= date.format('yyyyMMdd') &&
				span.to.format('yyyyMMdd') >= date.format('yyyyMMdd')
			) {
				result = true;
				if (span.editable) {
					span.from = null;
					span.to = null;
				}
			}
		});
		return result;
	}

	private validateSpanSelection(from: Date, to: Date): boolean {
		const spanSize = Math.round(Math.abs((from.setHours(0, 0, 0, 0) - to.setHours(0, 0, 0, 0)) / (24 * 60 * 60 * 1000))) + 1;
		return spanSize >= this.options().spanSize.min && spanSize <= this.options().spanSize.max;
	}

	private validateSpanIndex() {
		while (this.dateSpans[this.currentDateSpanIndex] && !this.dateSpans[this.currentDateSpanIndex].editable) {
			// Skip non editable fields
			this.increaseSpanIndex();
		}
		if (!this.dateSpans[this.currentDateSpanIndex]) this.dateSpans.push({ from: null, to: null, editable: true });
	}

	private increaseSpanIndex() {
		if (this.options().numberOfSpans === 1) return;

		this.currentDateSpanIndex = (this.currentDateSpanIndex + 1) % this.options().numberOfSpans;
	}

	deselect() {
		this.selectedDate = null;
		this.selectedDateText = null;
		this.currentView = null; // TODO: Set next if not minView
		this.emitValue(null);
	}

	monthStartDay(y: number, m: number): number {
		// Month start index
		const d = new Date();
		d.setDate(1);
		d.setMonth(m - 1);
		d.setFullYear(y);
		const idx = d.getDay() + (this.sunFirst ? 0 : 6);
		return idx >= 7 ? idx - 7 : idx;
	}

	daysInMonth(m: number, y: number): number {
		// Return number of days of current month
		return new Date(y, m, 0).getDate();
	}

	daysInPrevMonth(m: number, y: number): number {
		// Return number of days of the previous month
		if (m === 1) {
			m = 12;
			y--;
		} else {
			m--;
		}
		return this.daysInMonth(m, y);
	}

	isToday(date: Date): boolean {
		return (
			date.getDate() === this.today.getDate() &&
			date.getMonth() === this.today.getMonth() &&
			date.getFullYear() === this.today.getFullYear()
		);
	}

	isSelected(date: Date): boolean {
		return (
			this.selectedDate !== null &&
			date.getDate() === this.selectedDate.getDate() &&
			date.getMonth() === this.selectedDate.getMonth() &&
			date.getFullYear() === this.selectedDate.getFullYear()
		);
	}

	/**
	 * Check if class is within a datespan and return class accordingly
	 * @param date
	 */
	getDateSpanClasses(date: Date): string {
		let classString = '';
		this.dateSpans.forEach((span, index) => {
			const indexString = index === 0 ? (index + 1).toString() : 'x';
			if (
				span.from &&
				span.to &&
				span.from.format('yyyyMMdd') <= date.format('yyyyMMdd') &&
				span.to.format('yyyyMMdd') >= date.format('yyyyMMdd')
			) {
				classString = ` sp_${indexString}_middle`;
			}

			if (span.from && span.from.format('yyyyMMdd') === date.format('yyyyMMdd')) {
				classString += ` sp_${indexString}_start`;
			}

			// If no end selected and day is start, then it should get end border
			if (
				(span.to && span.to.format('yyyyMMdd') === date.format('yyyyMMdd')) ||
				(!span.to && span.from && span.from.format('yyyyMMdd') === date.format('yyyyMMdd'))
			) {
				classString += ` sp_${indexString}_end`;
			}
		});

		return classString;
	}

	/**
	 * Validate that date is within min/max date
	 * @param date
	 */
	isValid(date: Date): boolean {
		const valid1 = this.minDate === null || date.format('yyyyMMdd') >= (this.minDate as Date).format('yyyyMMdd');
		const valid2 = this.maxDate === null || date.format('yyyyMMdd') <= (this.maxDate as Date).format('yyyyMMdd');

		return valid1 && valid2;
	}

	createMonth(m: number, y: number): void {
		this.dates.length = 0;
		const monthStart = this.monthStartDay(y, m);
		const dInThisM = this.daysInMonth(m, y);
		const dInPrevM = this.daysInPrevMonth(m, y);
		const sunIdx = this.sunFirst ? 0 : 6;

		let dayNbr = 1;
		for (let i = 1; i <= 6; i++) {
			const week: Array<Day> = [];
			if (i === 1) {
				const pm = dInPrevM - monthStart + 1;
				// Previous month
				for (let j = pm; j <= dInPrevM; j++) {
					week.push({ date: new Date(y, m - 2, j), day: j, month: m - 1, year: y, sun: week.length === sunIdx });
				}
				// Current month
				const daysLeft = 7 - week.length;
				for (let j = 0; j < daysLeft; j++) {
					week.push({ date: new Date(y, m - 1, dayNbr), day: dayNbr, month: m, year: y, sun: week.length === sunIdx });
					dayNbr++;
				}
			} else {
				// Rest of the weeks
				for (let j = 1; j < 8; j++) {
					if (dayNbr > dInThisM) {
						// Next month
						dayNbr = 1;
						m++;
					}
					week.push({ date: new Date(y, m - 1, dayNbr), day: dayNbr, month: m, year: y, sun: week.length === sunIdx });
					dayNbr++;
				}
			}
			this.dates.push(week);
		}
	}

	handleErrors() {
		this.currentError = GlobalValidator.validateFormControl(
			this.translateService,
			this.controlDir.control as UntypedFormControl,
			this.errorMessages,
		);
	}

	/**
	 * Recreate so we always get the latest options
	 */
	public options() {
		return new DatepickerOptions(this.overrideOptions);
	}
}
